// src/NSCCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const NSCCalculator = () => {
  const [amountInvested, setAmountInvested] = useState(10000);
  const [interestRate, setInterestRate] = useState(6.8); // Example rate for NSC
  const [compoundFrequency, setCompoundFrequency] = useState('yearly');
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const calculateNSC = () => {
    const timePeriod = 5; // Fixed time period of 5 years
    let n;
    switch (compoundFrequency) {
      case 'quarterly':
        n = 4;
        break;
      case 'halfyearly':
        n = 2;
        break;
      case 'yearly':
      default:
        n = 1;
        break;
    }

    const rate = interestRate / 100;
    const amount = amountInvested * Math.pow((1 + rate / n), n * timePeriod);
    const interest = amount - amountInvested;

    setTotalInterest(interest.toFixed(2));
    setTotalAmount(amount.toFixed(2));
  };

  const handleAmountInvestedChange = (e) => {
    const value = e.target.value;
    setAmountInvested(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleCompoundFrequencyChange = (e) => {
    setCompoundFrequency(e.target.value);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'NSC Breakdown',
        data: [amountInvested, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is the minimum amount I can enter in the NSC calculator?',
        answer: '₹1,000 is the minimum investment for NSC, and the calculator accepts this as the starting amount.',
      },
      {
        question: 'How often is the interest rate updated in the calculator?',
        answer: 'We update the rate as soon as the Government of India revises it (usually quarterly).',
      },
      {
        question: 'Is the NSC calculator available on mobile?',
        answer: 'Yes, it\'s fully responsive and works seamlessly on all mobile browsers.',
      },
      {
        question: 'Does the calculator include tax deductions?',
        answer: 'The calculator shows gross maturity. You should consult your CA for exact post-tax calculations.',
      },
      {
        question: 'What if the interest rate changes in the future?',
        answer: 'Your investment is locked at the rate applicable on the day of purchase, which the calculator uses.',
      },
      {
        question: 'Can I download the NSC result?',
        answer: 'You can take a screenshot or print the result directly for your records.',
      },
      {
        question: 'Does NSC offer monthly or quarterly interest payouts?',
        answer: 'No, interest is compounded annually and paid only at maturity.',
      },
      {
        question: 'Can I use this calculator to plan multiple NSC purchases?',
        answer: 'Yes, use the tool separately for each investment amount and add them for combined maturity.',
      },
      {
        question: 'What if I miss claiming tax benefit on NSC?',
        answer: 'You can still claim the tax deduction while filing ITR, provided you have the certificates.',
      },
      {
        question: 'Is it better to invest in NSC at the start of the financial year?',
        answer: 'Yes, early investments help in maximizing compounding and better tax planning.',
      },
      {
        question: 'How safe is NSC as an investment?',
        answer: 'It is one of the safest, as it is fully backed by the Government of India.',
      },
      {
        question: 'Can NSC be held jointly?',
        answer: 'Yes, NSC allows joint holding, and the first holder can claim tax benefits.',
      },
      {
        question: 'Can minors invest in NSC?',
        answer: 'Yes, with a guardian. The guardian claims the tax benefits.',
      },
      {
        question: 'Is TDS applicable on NSC maturity?',
        answer: 'No TDS is deducted at source, but interest is taxable in the year of receipt.',
      },
      {
        question: 'What happens if I lose my NSC certificate?',
        answer: 'You can apply for a duplicate at the post office by submitting required documents.',
      },
    ]
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
    <Helmet>
         <title>NSC Calculator – National Savings Certificate Returns</title>
        <meta  name='description' content='Calculate maturity amount and interest on your NSC investments. Use this NSC calculator for better planning.'/>
        <meta name='keywords' content='nsc calculator, 5 year nsc interest rate chart, calculate nsc interest, national saving certificate calculator, national savings certificate interest rate calculator, nsc calculator post office, nsc interest rate calculator, nsc savings calculator' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/nsc-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">NSC Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4'>

    
    <div className="container mx-auto p-4 shadow-xl rounded-md ">
      <h1 className="text-2xl font-bold mb-4">NSC Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Amount Invested</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={amountInvested}
                onChange={handleAmountInvestedChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {amountInvested.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={amountInvested}
              onChange={(e) => setAmountInvested(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Compound Frequency</label>
            <select
              value={compoundFrequency}
              onChange={handleCompoundFrequencyChange}
              className="w-full p-2 border rounded"
            >
              <option value="yearly">Yearly</option>
              <option value="halfyearly">Half-Yearly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Time Period</label>
            <div className="flex items-center">
              <span className="text-gray-700">5 Years (Fixed)</span>
            </div>
          </div>
          <button
            onClick={calculateNSC}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Principal Amount: ₹ {amountInvested.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            TheFinMaster’s NSC Calculator: Plan Your Secure Investments with Ease
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The National Savings Certificate (NSC) Calculator is a simple yet powerful tool that helps you estimate the maturity value of your NSC investment. As a government-backed, fixed-income scheme with a 5-year lock-in and annual compounding, NSC returns can be complex to calculate manually. TheFinMaster’s NSC Calculator simplifies this process, providing accurate results instantly to help you plan your financial goals with confidence.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is an NSC Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The National Savings Certificate (NSC) Calculator is an online tool designed to calculate the maturity value of your NSC investment. It factors in the principal amount and the current NSC interest rate to provide a precise estimate of your total returns, including compounded interest, after the 5-year tenure. This tool eliminates manual calculations, enabling informed investment decisions with clarity.[](https://scripbox.com/plan/nsc-calculator/)
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Benefits of Using TheFinMaster's NSC Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using our NSC calculator offers several advantages for anyone considering NSC as part of their investment portfolio:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Effortless & Accurate Return Estimations:</strong> No need for manual calculations or formulas. The calculator applies annual compounding with the latest NSC interest rates for error-free results.</li>
              <li><strong>Smarter Investment Planning:</strong> Understand how your investment grows over 5 years, whether you’re investing ₹10,000 or ₹1,50,000, to make informed financial choices.</li>
              <li><strong>Visualize Compounded Growth:</strong> Gain clarity on how your money compounds annually, allowing you to compare NSC returns with other options like fixed deposits or PPF.</li>
              <li><strong>Instant Results, No Signup:</strong> No need to sign in or share personal data. Enter your investment amount and get your maturity value instantly.</li>
              <li><strong>Optimized for All Devices:</strong> Use the calculator on mobile, tablet, or desktop anytime, anywhere, with a fast and user-friendly interface.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does The NSC Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The NSC calculator uses the compound interest formula for a fixed 5-year tenure with annual compounding. The formula is:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Maturity Value (MV) = P × (1 + r)^n</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Principal amount (your investment)</li>
              <li>r = Annual interest rate (current NSC rate, e.g., 7.7%)</li>
              <li>n = Number of years (fixed at 5 years)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              For example, if you invest ₹1,00,000 at a 7.7% interest rate, the calculator will show a maturity value of approximately ₹1,45,815 after 5 years. TheFinMaster’s calculator applies this formula in real time, making the process seamless.[](https://www.shriramfinance.in/national-saving-calculator)
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is National Savings Certificate (NSC)?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The National Savings Certificate (NSC) is a fixed-income investment scheme offered by the Government of India through post offices. Key features include:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Guaranteed returns with annual compounding</li>
              <li>5-year lock-in period</li>
              <li>Tax benefits under Section 80C (up to ₹1.5 lakh)</li>
              <li>Minimum investment of ₹1,000, no upper limit</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              NSC is ideal for conservative investors prioritizing safety and predictable returns.[](https://www.indiafirstlife.com/knowledge-center/investment/national-savings-certificate)
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Real-Life Example of NSC Investment Growth
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Consider an investment of ₹1,50,000 at a 7.7% interest rate:
            </p>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Year</th>
                    <th className="px-4 py-2 text-left">Opening Balance</th>
                    <th className="px-4 py-2 text-left">Interest Earned</th>
                    <th className="px-4 py-2 text-left">Closing Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">1</td>
                    <td className="px-4 py-2">₹1,50,000</td>
                    <td className="px-4 py-2">₹11,550</td>
                    <td className="px-4 py-2">₹1,61,550</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">2</td>
                    <td className="px-4 py-2">₹1,61,550</td>
                    <td className="px-4 py-2">₹12,442</td>
                    <td className="px-4 py-2">₹1,73,992</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">3</td>
                    <td className="px-4 py-2">₹1,73,992</td>
                    <td className="px-4 py-2">₹13,397</td>
                    <td className="px-4 py-2">₹1,87,389</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">4</td>
                    <td className="px-4 py-2">₹1,87,389</td>
                    <td className="px-4 py-2">₹14,423</td>
                    <td className="px-4 py-2">₹2,01,812</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">5</td>
                    <td className="px-4 py-2">₹2,01,812</td>
                    <td className="px-4 py-2">₹15,539</td>
                    <td className="px-4 py-2">₹2,17,351</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="max-w-4xl mx-auto overflow-hidden rounded-lg border border-gray-300 shadow mt-6">
    {/* Header */}
    <div className="hidden md:grid grid-cols-4 bg-gray-800 text-white text-sm font-semibold text-center">
      <div className="px-3 py-2 border-r">Year</div>
      <div className="px-3 py-2 border-r">	Opening Balance</div>
      <div className="px-3 py-2 border-r">	Interest Earned</div>
   

      <div className="px-3 py-2">	Closing Balance</div>
    </div>

    {/* Data rows for mobile (stacked) and desktop (grid) */}
    <div className="divide-y divide-gray-200 text-sm md:text-base text-gray-600 ">
      {/* Row Template */}
      {[
        {
          fet: "1",
          sim: "	₹1,50,000",
          ct: "₹11,550",
          fd: "	₹1,61,550",
         
        },
        {
          fet: "2",
          sim: "	₹1,61,550",
          ct: "	₹12,442",
          fd: "			₹1,73,992",
         
        },
        {
          fet: "3",
          sim: "	₹1,73,992",
          ct: "	₹13,397",
          fd: "	₹1,87,389",
         
        },
        {
          fet: "4",
          sim: "	₹1,87,389",
          ct: "	₹14,423",
          fd: "	₹2,01,812",
         
        },
        {
          fet: "5",
          sim: "	₹2,01,812",
          ct: "	₹15,539",
          fd: "		₹2,17,351",
         
        },
      ].map((row, idx) => (
        <div
          key={idx}
          className="block md:grid md:grid-cols-4 hover:bg-gray-50 transition"
        >
          {/* Mobile version */}
          <div className="block md:hidden px-4 py-3">
            <p className="font-semibold">Year:<span className="font-normal">{row.fet}</span></p>
            <p className="font-semibold">Opening Balance: <span className="font-normal">{row.sim}</span></p>
            <p className="font-semibold">Interest Earned:<span className="font-normal">{row.ct}</span></p>
            <p className="font-semibold">	Closing Balance: <span className="font-normal">{row.fd}</span></p>
            
          </div>

          {/* Desktop version */}
          <div className="hidden md:block px-4 py-3 border-r">{row.fet}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.sim}</div>
          
          <div className="hidden md:block px-4 py-3">{row.ct}</div>
          <div className="hidden md:block px-4 py-3">{row.fd}</div>
        </div>
      ))}
    </div>
  </div>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Over 5 years, your ₹1.5 lakh grows to over ₹2.17 lakh — a safe, fixed return ideal for medium-term savings.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefits of NSC
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              NSC offers significant tax advantages:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Investments up to ₹1.5 lakh per year qualify for tax deduction under Section 80C.</li>
              <li>Interest accrued annually is considered reinvested and eligible for Section 80C benefits (except in the final year).</li>
              <li>The maturity amount’s interest in the final year is taxable under “Income from Other Sources.”</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator helps you plan your investments to maximize tax efficiency.[](https://www.paisabazaar.com/saving-schemes/national-savings-certificate/)
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Use an NSC Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The NSC calculator is ideal for:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Salaried individuals planning year-end tax-saving investments</li>
              <li>First-time investors seeking a risk-free option</li>
              <li>Parents planning secure, long-term funds for children</li>
              <li>Retirees looking for guaranteed returns with capital protection</li>
              <li>Anyone comparing fixed-income products before investing</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              NSC Calculator vs. SIP & FD Calculators
            </h1>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Feature</th>
                    <th className="px-4 py-2 text-left">NSC Calculator</th>
                    <th className="px-4 py-2 text-left">SIP Calculator</th>
                    <th className="px-4 py-2 text-left">FD Calculator</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">Investment Type</td>
                    <td className="px-4 py-2">Lump sum only</td>
                    <td className="px-4 py-2">Monthly contributions</td>
                    <td className="px-4 py-2">Lump sum</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Tenure</td>
                    <td className="px-4 py-2">Fixed (5 years)</td>
                    <td className="px-4 py-2">Flexible (user-defined)</td>
                    <td className="px-4 py-2">Flexible</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Returns</td>
                    <td className="px-4 py-2">Compounded annually</td>
                    <td className="px-4 py-2">Market-linked (mutual funds)</td>
                    <td className="px-4 py-2">Fixed (bank defined)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Risk Level</td>
                    <td className="px-4 py-2">Very low (Govt.)</td>
                    <td className="px-4 py-2">Moderate to High</td>
                    <td className="px-4 py-2">Low to Moderate</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Tax Benefits</td>
                    <td className="px-4 py-2">Under 80C</td>
                    <td className="px-4 py-2">Under 80C (ELSS only)</td>
                    <td className="px-4 py-2">Limited to 5-year FD</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
                <div className="max-w-4xl mx-auto overflow-hidden rounded-lg border border-gray-300 shadow mt-6">
    {/* Header */}
    <div className="hidden md:grid grid-cols-4 bg-gray-800 text-white text-sm font-semibold text-center">
      <div className="px-3 py-2 border-r">Feature</div>
      <div className="px-3 py-2 border-r">	NSC Calculator</div>
      <div className="px-3 py-2 border-r">	SIP Calculator</div>
   

      <div className="px-3 py-2">	FD Calculator</div>
    </div>

    {/* Data rows for mobile (stacked) and desktop (grid) */}
    <div className="divide-y divide-gray-200 text-sm md:text-base text-gray-600 ">
      {/* Row Template */}
      {[
        {
          fet: "Investment Type",
          sim: "	Lump sum only",
          ct: "Monthly contributions",
          fd: "	Lump sum",
         
        },
        {
          fet: "Tenure",
          sim: "	Fixed (5 years)",
          ct: "	Flexible (user-defined)",
          fd: "		Flexible",
         
        },
        {
          fet: "Returns",
          sim: "	Compounded annually",
          ct: "	Market-linked (mutual funds)",
          fd: "	Fixed (bank defined)",
         
        },
        {
          fet: "Risk Level",
          sim: "	Very low (Govt.)",
          ct: "	Moderate to High",
          fd: "	Low to Moderate",
         
        },
        {
          fet: "Tax Benefits",
          sim: "	Under 80C",
          ct: "	Under 80C (ELSS only)",
          fd: "		Limited to 5-year FD",
         
        },
      ].map((row, idx) => (
        <div
          key={idx}
          className="block md:grid md:grid-cols-4 hover:bg-gray-50 transition"
        >
          {/* Mobile version */}
          <div className="block md:hidden px-4 py-3">
            <p className="font-semibold">Feature <span className="font-normal">{row.fet}</span></p>
            <p className="font-semibold">	NSC Calculator <span className="font-normal">{row.sim}</span></p>
            <p className="font-semibold">SIP Calculator<span className="font-normal">{row.ct}</span></p>
            <p className="font-semibold">	FD Calculator <span className="font-normal">{row.fd}</span></p>
            
          </div>

          {/* Desktop version */}
          <div className="hidden md:block px-4 py-3 border-r">{row.fet}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.sim}</div>
          
          <div className="hidden md:block px-4 py-3">{row.ct}</div>
          <div className="hidden md:block px-4 py-3">{row.fd}</div>
        </div>
      ))}
    </div>
  </div>
          </div>
      
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Features of TheFinMaster NSC Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Clean & intuitive interface</li>
              <li>Based on real-time interest rates</li>
              <li>Compounding logic built-in</li>
              <li>100% free to use</li>
              <li>Helps compare with other saving options</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Start Planning with TheFinMaster’s NSC Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you’re saving for a medium-term goal, planning tax-efficient investments, or comparing fixed-income options, TheFinMaster’s NSC Calculator is your go-to tool. It’s fast, accurate, and designed to empower you with clear financial insights. Try it today and take control of your savings journey!
            </p>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default NSCCalculator;
